// Medical Terms Glossary
// Plain-language definitions for complex medical terms
// Reading level: 8th-10th grade

export const medicalTerms = {
  // Anatomy
  "prostate": {
    term: "Prostate",
    definition: "A small gland below the bladder in men. It makes fluid that helps carry sperm.",
    anatomyPage: "/anatomy/prostate"
  },
  "bladder": {
    term: "Bladder",
    definition: "The organ that stores urine (pee) until you are ready to empty it.",
    anatomyPage: "/anatomy/bladder"
  },
  "kidney": {
    term: "Kidney",
    definition: "Two organs in your back that filter blood and make urine. They remove waste from your body.",
    anatomyPage: "/anatomy/kidney"
  },
  "kidneys": {
    term: "Kidneys",
    definition: "Two organs in your back that filter blood and make urine. They remove waste from your body.",
    anatomyPage: "/anatomy/kidney"
  },
  "ureter": {
    term: "Ureter",
    definition: "A thin tube that carries urine from each kidney down to your bladder.",
    anatomyPage: "/anatomy/ureter"
  },
  "ureters": {
    term: "Ureters",
    definition: "Thin tubes that carry urine from each kidney down to your bladder. You have two, one from each kidney.",
    anatomyPage: "/anatomy/ureter"
  },
  "urethra": {
    term: "Urethra",
    definition: "The tube that carries urine from your bladder out of your body when you urinate.",
    anatomyPage: "/anatomy/urethra"
  },
  "testicle": {
    term: "Testicle",
    definition: "One of two oval glands in men that make sperm and hormones. Also called a testis.",
    anatomyPage: "/anatomy/testicle"
  },
  "testicles": {
    term: "Testicles",
    definition: "Two oval glands in men that make sperm and hormones. Also called testes.",
    anatomyPage: "/anatomy/testicle"
  },
  "scrotum": {
    term: "Scrotum",
    definition: "The skin pouch that holds the testicles outside the body.",
    anatomyPage: "/anatomy/testicle"
  },
  "epididymis": {
    term: "Epididymis",
    definition: "A coiled tube behind each testicle that stores and carries sperm.",
    anatomyPage: "/anatomy/testicle"
  },
  "bladder neck": {
    term: "Bladder Neck",
    definition: "The area where the bladder connects to the urethra. Muscles here control when you urinate.",
    anatomyPage: "/anatomy/bladder"
  },
  "vas deferens": {
    term: "Vas Deferens",
    definition: "A tube that carries sperm from the testicle to the urethra during ejaculation.",
    anatomyPage: "/anatomy/testicle"
  },
  "seminal vesicles": {
    term: "Seminal Vesicles",
    definition: "Two small glands that make fluid to nourish and carry sperm.",
    anatomyPage: "/anatomy/prostate"
  },
  "renal": {
    term: "Renal",
    definition: "A medical word meaning 'related to the kidneys.'",
    anatomyPage: "/anatomy/kidney"
  },

  // Conditions
  "bph": {
    term: "BPH",
    definition: "Benign Prostatic Hyperplasia. A non-cancerous enlargement of the prostate that can make urination difficult.",
    anatomyPage: "/anatomy/prostate"
  },
  "benign prostatic hyperplasia": {
    term: "Benign Prostatic Hyperplasia",
    definition: "A non-cancerous enlargement of the prostate gland. Common in older men. Can cause urination problems.",
    anatomyPage: "/anatomy/prostate"
  },
  "hydronephrosis": {
    term: "Hydronephrosis",
    definition: "Swelling of a kidney when urine backs up because it cannot drain properly.",
    anatomyPage: "/anatomy/kidney"
  },
  "hematuria": {
    term: "Hematuria",
    definition: "Blood in the urine. It may be visible (red or pink urine) or only seen under a microscope.",
    anatomyPage: "/anatomy/bladder"
  },
  "urinary retention": {
    term: "Urinary Retention",
    definition: "When you cannot fully empty your bladder, or cannot urinate at all.",
    anatomyPage: "/anatomy/bladder"
  },
  "uti": {
    term: "UTI",
    definition: "Urinary Tract Infection. An infection in the bladder, urethra, or kidneys caused by bacteria.",
    anatomyPage: "/anatomy/bladder"
  },
  "urinary tract infection": {
    term: "Urinary Tract Infection",
    definition: "An infection in the bladder, urethra, or kidneys caused by bacteria. Common symptoms include burning with urination.",
    anatomyPage: "/anatomy/bladder"
  },
  "kidney stone": {
    term: "Kidney Stone",
    definition: "A hard mineral deposit that forms in the kidney. Can cause severe pain when it moves through the urinary tract.",
    anatomyPage: "/anatomy/kidney"
  },
  "kidney stones": {
    term: "Kidney Stones",
    definition: "Hard mineral deposits that form in the kidneys. Can cause severe pain when they move through the urinary tract.",
    anatomyPage: "/anatomy/kidney"
  },
  "incontinence": {
    term: "Incontinence",
    definition: "Loss of bladder control, leading to accidental leakage of urine.",
    anatomyPage: "/anatomy/bladder"
  },
  "erectile dysfunction": {
    term: "Erectile Dysfunction",
    definition: "Difficulty getting or keeping an erection firm enough for sex. Also called ED.",
    anatomyPage: "/anatomy/prostate"
  },
  "psa": {
    term: "PSA",
    definition: "Prostate-Specific Antigen. A protein made by the prostate. A blood test measures PSA levels to check prostate health.",
    anatomyPage: "/anatomy/prostate"
  },
  "overactive bladder": {
    term: "Overactive Bladder",
    definition: "A condition causing sudden, frequent urges to urinate that are hard to control.",
    anatomyPage: "/anatomy/bladder"
  },

  // Procedures
  "cystoscopy": {
    term: "Cystoscopy",
    definition: "A procedure where a doctor uses a thin camera to look inside your bladder and urethra.",
    anatomyPage: "/anatomy/bladder"
  },
  "ureteroscopy": {
    term: "Ureteroscopy",
    definition: "A procedure where a doctor uses a thin scope to look inside your ureter and kidney, often to remove stones.",
    anatomyPage: "/anatomy/ureter"
  },
  "lithotripsy": {
    term: "Lithotripsy",
    definition: "A treatment that uses shock waves or lasers to break kidney stones into smaller pieces.",
    anatomyPage: "/anatomy/kidney"
  },
  "turp": {
    term: "TURP",
    definition: "Transurethral Resection of the Prostate. A surgery to remove prostate tissue blocking urine flow.",
    anatomyPage: "/anatomy/prostate"
  },
  "biopsy": {
    term: "Biopsy",
    definition: "A procedure where a small tissue sample is removed and checked for disease, such as cancer.",
    anatomyPage: null
  },
  "prostate biopsy": {
    term: "Prostate Biopsy",
    definition: "A procedure where small tissue samples are taken from the prostate to check for cancer.",
    anatomyPage: "/anatomy/prostate"
  },
  "vasectomy": {
    term: "Vasectomy",
    definition: "A minor surgery for permanent birth control in men. The tubes carrying sperm are cut or blocked.",
    anatomyPage: "/anatomy/testicle"
  },
  "catheter": {
    term: "Catheter",
    definition: "A thin, flexible tube inserted into the bladder to drain urine.",
    anatomyPage: "/anatomy/bladder"
  },
  "foley catheter": {
    term: "Foley Catheter",
    definition: "A tube inserted into the bladder that stays in place to continuously drain urine into a bag.",
    anatomyPage: "/anatomy/bladder"
  },
  "stent": {
    term: "Stent",
    definition: "A small tube placed inside the body to keep a passage open. In urology, often placed in the ureter.",
    anatomyPage: "/anatomy/ureter"
  },
  "ureteral stent": {
    term: "Ureteral Stent",
    definition: "A small tube placed in the ureter to help urine flow from the kidney to the bladder.",
    anatomyPage: "/anatomy/ureter"
  },

  // Medical terms
  "endoscopic": {
    term: "Endoscopic",
    definition: "Using a thin, flexible camera to look inside the body without major surgery.",
    anatomyPage: null
  },
  "malignant": {
    term: "Malignant",
    definition: "Cancerous. A tumor that can grow and spread to other parts of the body.",
    anatomyPage: null
  },
  "benign": {
    term: "Benign",
    definition: "Not cancerous. A growth that does not spread to other parts of the body.",
    anatomyPage: null
  },
  "metastasis": {
    term: "Metastasis",
    definition: "When cancer spreads from where it started to another part of the body.",
    anatomyPage: null
  },
  "urologist": {
    term: "Urologist",
    definition: "A doctor who specializes in the urinary system and male reproductive organs.",
    anatomyPage: null
  },
  "ultrasound": {
    term: "Ultrasound",
    definition: "A test that uses sound waves to create pictures of organs inside your body. It is painless.",
    anatomyPage: null
  },
  "ct scan": {
    term: "CT Scan",
    definition: "A detailed X-ray that takes pictures of the inside of your body from many angles.",
    anatomyPage: null
  },
  "mri": {
    term: "MRI",
    definition: "Magnetic Resonance Imaging. A scan that uses magnets to create detailed pictures inside your body.",
    anatomyPage: null
  },
  "anesthesia": {
    term: "Anesthesia",
    definition: "Medicine that prevents pain during procedures. Can make you sleepy or numb a specific area.",
    anatomyPage: null
  },
  "local anesthesia": {
    term: "Local Anesthesia",
    definition: "Medicine that numbs only a small area of your body so you do not feel pain there.",
    anatomyPage: null
  },
  "general anesthesia": {
    term: "General Anesthesia",
    definition: "Medicine that puts you into a deep sleep during surgery so you feel nothing.",
    anatomyPage: null
  },
  "antibiotic": {
    term: "Antibiotic",
    definition: "Medicine that kills bacteria to treat or prevent infections.",
    anatomyPage: null
  },
  "antibiotics": {
    term: "Antibiotics",
    definition: "Medicines that kill bacteria to treat or prevent infections.",
    anatomyPage: null
  }
};

// Function to get all terms as a sorted array for matching
export const getMedicalTermsList = () => {
  return Object.keys(medicalTerms).sort((a, b) => b.length - a.length);
};

// Function to get term info
export const getTermInfo = (term) => {
  const lowerTerm = term.toLowerCase();
  return medicalTerms[lowerTerm] || null;
};
